import { useQuery } from '@tanstack/react-query';
import type { InsightsPlatformRow } from '@brand24-original/llm-types';
import { GET_INSIGHTS_PLATFORMS } from '../../query/insights/keys';
import { getInsightsPlatforms } from '../../services/insights/InsightsStatsService';
import { ChatType } from '../../../types/monitor/ChatType';

export interface InsightsPlatformsParams {
  projectId: number;
  chatType: ChatType;
  from: Date | null;
  to: Date | null;
  monitorPromptIds?: number[];
  filterChatTypes?: string[];
  /**
   * The analyzed brand (project.firmName). Key-only: the backend reads it from the project, this just
   * forces a refetch when the ChangeKeywordBox switches it.
   */
  brand?: string;
}

export const useGetInsightsPlatforms = (params: InsightsPlatformsParams | null) => {
  const fromTimestamp = params?.from?.getTime() ?? null;
  const toTimestamp = params?.to?.getTime() ?? null;

  const query = useQuery<InsightsPlatformRow[]>({
    queryKey: [
      GET_INSIGHTS_PLATFORMS,
      params?.projectId,
      params?.chatType,
      fromTimestamp,
      toTimestamp,
      params?.monitorPromptIds,
      params?.filterChatTypes,
      params?.brand,
    ],
    // `enabled` gates on params/from/to, so the assertions below cannot fire at runtime.
    queryFn: () =>
      getInsightsPlatforms(
        params!.projectId,
        params!.chatType,
        params!.from!,
        params!.to!,
        params!.monitorPromptIds,
        params!.filterChatTypes
      ),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: !!params && !!params.from && !!params.to && !!params.chatType && !!params.projectId,
  });

  return {
    insightsPlatforms: query.data ?? null,
    // isPending, not isLoading — patrz komentarz w useGetInsightsOverview.
    isLoading: query.isPending,
    isError: query.isError,
  };
};
